import {
  Autocomplete,
  InputAdornment,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { useEasierTheme } from "datalign-themes";
import { FacetFields } from "models/facet-fields";
import Input from "models/input/input";
import React from "react";
import { useNavigate } from "react-router-dom";
import SearchBarFacets from "../search-bar-facets";

const NTSearchBar = ({
  facets,
  isLoading,
  inputsList,
  searchTerm,
  open,
  handleOpen,
  handleSearchTerm,
  handleSearch,
}: {
  facets: FacetFields;
  isLoading: boolean;
  inputsList: Input[];
  searchTerm: string;
  open: boolean;
  handleOpen: (open: boolean) => void;
  handleSearchTerm: (searchTerm: string) => void;
  handleSearch: () => void;
}) => {
  const navigate = useNavigate();
  const theme = useEasierTheme();

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        mx: "auto",
        my: 2,
        px: { xs: 2, sm: 4 }, // responsive horizontal padding
        py: { xs: 1, sm: 2 },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" }, // stack vertically on small screens
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "center" },
        gap: 2, // spacing between items when stacked
        background: "white",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          color={theme?.Colour.Primary()}
          fontSize={{ xs: "1rem", sm: "1.2rem" }}
          fontWeight={450}
          mb={1}
        >
          Search data assets
        </Typography>
        <Autocomplete
          id="search-bar"
          loading={isLoading}
          disableClearable
          clearOnBlur={false}
          options={inputsList}
          inputValue={searchTerm ?? ""}
          getOptionLabel={(option) => option?.title ?? ""}
          onChange={(_e, newValue) =>
            newValue?.input_id && navigate(`/view/${newValue.input_id}`)
          }
          onInputChange={(_e, newValue) => handleSearchTerm(newValue ?? "")}
          onKeyDown={(event) => {
            if (event.code === "Enter") {
              handleSearch();
            }
          }}
          filterOptions={(x) => x}
          sx={{
            width: "100%",
            minWidth: { xs: "100%", sm: 300 },
            "&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root": {
              padding: "8px",
            },
          }}
          open={open} // Control dropdown visibility manually
          onOpen={() => handleOpen(true)}
          onClose={(event, reason) => {
            // Prevent closing if clicked inside the dropdown
            if (reason === "escape" || reason === "blur") {
              handleOpen(false);
            }
          }}
          disableCloseOnSelect
          slots={{
            paper(props) {
              return (
                <SearchBarFacets
                  props={props}
                  searchTerm={searchTerm}
                  facets={facets}
                  handleOpen={handleOpen}
                />
              );
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              hiddenLabel
              placeholder="Type to search..."
              InputProps={{
                ...params.InputProps,
                type: "search",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      className="material-symbols-outlined"
                      aria-label="search"
                      onClick={() => {
                        handleSearch();
                      }}
                    >
                      search
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                sx: {
                  ml: 1,
                },
              }}
            />
          )}
        />
      </Box>
      <Box sx={{ flexShrink: 0, mt: { xs: 2, sm: 0 } }}>
        <Typography
          color={theme?.Colour.Primary()}
          fontSize={{ xs: "1rem", sm: "1.2rem" }}
          fontWeight={450}
          mb={1}
        >
          Explore data assets
        </Typography>
        <Button
          size={"small"}
          variant={"contained"}
          onClick={() => navigate("/explorer")}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            width: "100%",
            height: 40,
          }}
        >
          Launch Explorer
        </Button>
      </Box>
    </Box>
  );
};

export default NTSearchBar;
